import { IcVolumeOff, IcVolumeOn } from '@assets/icons/24x24';
import { Icon } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectHighestNotProcessedNotification } from '@redux/data/data.selectors';
import { getNotificationSound } from '@utils/data/notification.utils';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

export default function NotificationSounds() {
  const highestPriorityNotification = useAppSelector(selectHighestNotProcessedNotification, isEqual);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [userInteracted, setUserInteracted] = useState(false);

  const audioSrc = useMemo(() => {
    if (highestPriorityNotification === null) {
      return null;
    }
    return getNotificationSound(highestPriorityNotification);
  }, [highestPriorityNotification]);

  useEffect(() => {
    const handleUserInteraction = () => {
      setUserInteracted(true);
    };
    window.addEventListener('click', handleUserInteraction);

    return () => {
      window.removeEventListener('click', handleUserInteraction);
    };
  }, []);

  useEffect(() => {
    if (!audioSrc) {
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const audio = new Audio(audioSrc);
    audio.loop = true;
    audio.volume = 0.4;
    audio.muted = isMuted;
    audio.play();

    audioRef.current = audio;

    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, [audioSrc, isMuted, userInteracted]);

  return (
    <>
      <Icon
        as={isMuted ? IcVolumeOff : IcVolumeOn}
        width="24px"
        height="24px"
        color="neutral.300"
        cursor="pointer"
        _hover={{ color: 'skyBlue' }}
        onClick={() => setIsMuted(!isMuted)}
      />
    </>
  );
}
